// Task 7.5: FAQ Component with Accessibility

'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Card } from '@/components/ui/card';

interface FAQItem {
  question: string;
  answer: string;
}

export function FAQ() {
  const t = useTranslations('marketing.faq');
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const rawItems = (t as unknown as { raw?: (key: string) => unknown }).raw?.('items');
  const faqItems = (rawItems as FAQItem[] | undefined) ?? [];

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('title')}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t('subtitle')}
            </p>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => {
              const isSetup = item.question.toLowerCase().includes("what's included in setup") || item.question.toLowerCase().includes('whats included in setup');
              const isOpen = openItems.has(index);
              const itemId = `faq-item-${index}`;
              const contentId = `faq-content-${index}`;
              
              return (
                <Card key={index} className="p-6" id={isSetup ? 'faq-setup' : undefined}>
                  <button
                    type="button"
                    className="w-full text-left flex justify-between items-start gap-4 group"
                    onClick={() => toggleItem(index)}
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    id={itemId}
                  >
                    <h3 className="text-lg font-semibold group-hover:text-primary transition-colors">
                      {item.question}
                    </h3>
                    <span className={`text-2xl transition-transform ${isOpen ? 'rotate-45' : ''}`}>
                      +
                    </span>
                  </button>
                  
                  <div
                    id={contentId}
                    role="region"
                    aria-labelledby={itemId}
                    className={`overflow-hidden transition-all duration-200 ${
                      isOpen ? 'mt-4 opacity-100' : 'mt-0 opacity-0 max-h-0'
                    }`}
                  >
                    <div className="text-muted-foreground leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-4">
              {t('contact.question')}
            </p>
            <a 
              href="mailto:sales@scopax.com"
              className="text-primary hover:underline font-medium"
            >
              {t('contact.cta')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
